import React from "react";
import { Badge, Button, Tag, AlertCircle, CheckCircle, Edit01, Trash04 } from "@opendate-app/setlist";
import { useSeriesEditContext } from "../SeriesEditApp";
import { activityRows, describeTiming, timingOf } from "../seriesActivities";
import ShowActivityTimingTable from "./ShowActivityTimingTable";
import StepFooter from "./StepFooter";
import { toastError } from "../../../shared/toastHelper";
import PublishingCard, { publishingProblems } from "./PublishingCard";
import PublishCheckModal from "./PublishCheckModal";
import { SetDot } from "./TicketSetTabs";
import {
  formatDateLabel,
  formatTimeLabel,
  endTimeOf,
  endsNextDay,
  configFor,
  showTypesFor,
  inventoryLabel,
  withoutTicketType,
  ticketTypeReferences,
  deletionBlockedMessage,
  TIMED_ENTRY_SHOW_TYPE,
  UNLABELLED_SHOW_TYPE,
} from "../scheduleBuilder";
import {
  colorOf,
  coverage,
  describeAdjustment,
  describeRule,
  formatTimes,
  ticketSetsOf,
  typesInSet,
  usableAdjustment,
} from "../ticketSets";

const formatPrice = (price) => {
  const amount = Number(price);
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
};

const ReviewSection = () => {
  const { form, options, schedule, tickets, setTickets, goToStep, save, saving, canSave,
    configuredTicketTypes, publish, publishErrors, canPublish, seatingCharts, addOnSeatingCharts,
    publishWarnings = [], dismissPublishWarnings,
    extraShowDetailNames = [], ticketSetProblems = [] } = useSeriesEditContext();
  const { generated, mode } = schedule;
  const { byShowType, seatingMode } = tickets;
  const showTypes = showTypesFor(schedule);

  const showTypeFor = (row) =>
    mode === "timed_entry" ? TIMED_ENTRY_SHOW_TYPE : row.label || UNLABELLED_SHOW_TYPE;

  const removeTicketType = (showType, typeId) => {
    const config = configFor(byShowType, showType);
    const removed = config.ticketTypes.find((candidate) => candidate.id === typeId);
    const remaining = config.ticketTypes.filter((candidate) => candidate.id !== typeId);
    const blocked = removed && deletionBlockedMessage(config, ticketTypeReferences(config, remaining, removed.name), removed.name);
    if (blocked) {
      toastError(blocked);
      return;
    }
    setTickets((current) => ({
      ...current,
      byShowType: { ...current.byShowType, [showType]: withoutTicketType(configFor(current.byShowType, showType), typeId) },
    }));
  };

  const timed = mode === "timed_entry";
  const timedConfig = configFor(byShowType, TIMED_ENTRY_SHOW_TYPE);
  const sets = React.useMemo(() => (timed ? ticketSetsOf({ ticketSets: timedConfig.ticketSets }) : []), [timed, timedConfig.ticketSets]);
  const cover = React.useMemo(
    () => (timed ? coverage(schedule, sets, form.ageRestriction) : null),
    [timed, schedule, sets, form.ageRestriction],
  );
  const ticketGroups = timed
    ? sets.map((set) => ({ id: set.key, set, showType: TIMED_ENTRY_SHOW_TYPE, ticketTypes: typesInSet(timedConfig.ticketTypes, sets, set.key) }))
    : showTypes.map((showType) => ({ id: showType, showType, ticketTypes: configFor(byShowType, showType).ticketTypes }));

  const eventType = options.eventTypes.find((type) => String(type.value) === String(form.eventTypeTagId));
  const timedActivities = activityRows(form, extraShowDetailNames)
    .map((row) => ({ ...row, timing: timingOf(form, row) }))
    .filter((row) => row.timing);
  const scheduledActivities = timedActivities.filter((row) => row.column);
  const seated = seatingMode === "seated";
  const seatingLabel = seated ? "Seated" : "General Admission";
  const sectionSeats = (showType) =>
    configFor(byShowType, showType).ticketTypes.reduce((seats, type) => seats + (Number(type.inventory) || 0), 0);
  const uniqueLabels = (labels) => [...new Set(labels.filter(Boolean))].join(" / ");
  const capacityLabel =
    (seated
      ? uniqueLabels(showTypes.map((showType) => sectionSeats(showType) || null))
      : mode === "timed_entry"
        ? schedule.entry.capacity && `${schedule.entry.capacity} per entry time`
        : tickets.capacity) || "—";
  const chartLabel =
    uniqueLabels(
      showTypes.map(
        (showType) => (seatingCharts.find((chart) => chart.id === configFor(byShowType, showType).seatingChartId) || {}).name,
      ),
    ) || "None chosen";
  const addOnChartLabel = uniqueLabels(
    showTypes.map(
      (showType) => (addOnSeatingCharts.find((chart) => chart.id === configFor(byShowType, showType).addOnSeatingChartId) || {}).name,
    ),
  );
  const unpricedSectionAddOns =
    showTypes.some((showType) => {
      const config = configFor(byShowType, showType);
      if (!addOnSeatingCharts.some((chart) => chart.id === config.addOnSeatingChartId)) return false;
      return config.addOns.some((addOn) => addOn.sectionKey && !Number(addOn.price));
    });

  const missing = [
    [!(form.name || "").trim(), "Title is required"],
    [!form.eventTypeTagId, "Type is required"],
    [!form.venueName, "Venue is required"],
    [scheduledActivities.length === 0, "At least one show activity is required"],
    [generated.length === 0, "Generate a schedule using the rule builder"],
    [configuredTicketTypes.length === 0, "Configure ticket types for your schedule"],
    [
      !timed && showTypes.some((type) => configFor(byShowType, type).ticketTypes.length === 0),
      "Configure ticket types for every show type",
    ],
    [
      configuredTicketTypes.length > 0 &&
        configuredTicketTypes.some((type) => !(type.name || "").trim()),
      "Name every ticket type",
    ],
    [unpricedSectionAddOns, "Price every seated add-on"],
  ]
    .filter(([failed]) => failed)
    .map(([, message]) => message)
    .concat(timed && configuredTicketTypes.length > 0 ? ticketSetProblems : [])
    .concat(publishingProblems(form));

  const blocking = (publishErrors || []).length > 0 ? publishErrors : missing;

  const unitLabel = mode === "multiple_shows" ? "show" : "date";
  const ticketTypeCountFor = (row) =>
    timed
      ? ((cover.dates[row.date] || {}).keys || []).reduce((count, key) => count + typesInSet(timedConfig.ticketTypes, sets, key).length, 0)
      : configFor(byShowType, showTypeFor(row)).ticketTypes.length;

  return (
    <div className="series-edit-card">
      <h2 className="series-edit-section-title">Review &amp; Create</h2>
      <p className="series-edit-section-subtitle">Verify your series settings before creating.</p>

      {blocking.length > 0 && (
        <div className="series-notice warning series-review-missing">
          <AlertCircle width={20} height={20} aria-hidden="true" className="series-notice-icon" />
          <div>
            <div className="series-notice-title">Required information missing</div>
            <ul className="series-review-missing-list">
              {blocking.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {form.imageUrl && (
        <div className="series-review-hero" style={{ backgroundImage: `url(${form.imageUrl})` }}>
          <span className="series-review-hero-label">Series Photo</span>
        </div>
      )}

      <div className="series-review-columns">
        <div>
          <div className="series-review-heading">Series</div>
          <div className="series-review-name">{form.name || "Untitled"}</div>
          {form.subtitle && <div className="series-review-subtitle">{form.subtitle}</div>}
          <div className="series-review-tags">
            <Tag>{eventType ? eventType.label : "No type"}</Tag>
            <Tag>{form.venueName || "No venue"}</Tag>
            <Tag>{form.ageRestriction || "No age restriction"}</Tag>
          </div>
        </div>

        <div>
          <div className="series-review-heading">Show Activities ({timedActivities.length})</div>
          <ul className="series-review-list">
            {timedActivities.map((activity) => (
              <li key={activity.name} className="series-review-list-row">
                <span>{activity.name}</span>
                <span className="series-review-list-meta">{describeTiming(activity.timing, form.venueTimeZone)}</span>
              </li>
            ))}
          </ul>

          <div className="series-review-seating" data-testid="review-seating">
            <strong>{seatingLabel}</strong>
            <span>
              Capacity: <strong>{capacityLabel}</strong>
            </span>
            {seatingMode === "seated" && (
              <span>
                Chart: <strong>{chartLabel}</strong>
              </span>
            )}
            {addOnChartLabel && (
              <span>
                Add-on chart: <strong>{addOnChartLabel}</strong>
              </span>
            )}
          </div>
          <div className="series-review-heading">Ticket Types ({configuredTicketTypes.length})</div>
          {ticketGroups.map(({ id, set, showType, ticketTypes: groupTypes }) => (
            <div key={id} className="series-review-show-type" data-testid={set ? "review-ticket-set" : undefined}>
              {set ? (
                <>
                  <div className="series-review-set-header">
                    <SetDot color={colorOf(sets, set.key)} />
                    <span className="series-review-show-type-name">{set.name}</span>
                    <span className="series-review-list-meta">
                      {describeRule(set)} &middot; {cover.dateCounts[set.key] || 0} date{cover.dateCounts[set.key] === 1 ? "" : "s"}
                    </span>
                    {set.ageRestriction && <Badge size="sm">{set.ageRestriction}</Badge>}
                  </div>
                  {(set.priceAdjustments || [])
                    .filter((adjustment) => usableAdjustment(adjustment) && (adjustment.times || []).length > 0)
                    .map((adjustment) => (
                      <div key={adjustment.id} className="series-review-set-adjustment">
                        {adjustment.label || "Price adjustment"}: {describeAdjustment(adjustment)} at {formatTimes(adjustment.times)}
                      </div>
                    ))}
                </>
              ) : (
                showTypes.length > 1 && <div className="series-review-show-type-name">{showType}</div>
              )}
              <ul className="series-review-list">
                {groupTypes.map((type) => (
                  <li key={type.id} className="series-review-list-row series-review-list-row-boxed">
                    <span>{type.name || "Untitled ticket"}</span>
                    <span className="series-review-list-price">
                      {Number(type.price) ? formatPrice(type.price) : type.sectionKey && !type.free ? "Set a price" : "$0"}
                    </span>
                    <span className="series-review-list-meta">
                      {type.inventory === "" || type.inventory == null ? inventoryLabel(type, schedule) : `Qty: ${type.inventory}`}
                    </span>
                    <span className="series-row-actions">
                      <Button
                        variant="tertiary"
                        size="sm"
                        icon={Edit01}
                        aria-label={`Edit ${type.name || "ticket type"}`}
                        onClick={() => goToStep("tickets", set ? { ticketSetKey: set.key } : null)}
                      />
                      {!type.sectionKey && (
                        <Button
                          variant="tertiary"
                          size="sm"
                          icon={Trash04}
                          className="series-row-action-destructive"
                          aria-label={`Remove ${type.name || "ticket type"}`}
                          onClick={() => removeTicketType(showType, type.id)}
                        />
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <ShowActivityTimingTable activities={timedActivities} generated={generated} timeZone={form.venueTimeZone} />

      <div className="series-review-heading series-review-heading-spaced">
        Generated Schedule ({generated.length} {generated.length === 1 ? unitLabel : `${unitLabel}s`})
      </div>
      <ul
        className={["series-review-schedule", generated.length > 0 ? "series-review-schedule-boxed" : ""]
          .join(" ")
          .trim()}
      >
        {generated.map((row, index) => (
          <li key={row.id} className="series-review-schedule-row">
            <span className="series-review-schedule-index">{index + 1}</span>
            <span className="series-review-schedule-date">
              {formatDateLabel(row.date)}
              {mode === "multiple_shows" && row.label && (
                <span className="series-review-schedule-label"> ({row.label})</span>
              )}
            </span>
            <span className="series-review-list-meta">
              {mode === "timed_entry"
                ? `${row.times.length} entry ${row.times.length === 1 ? "slot" : "slots"}`
                : `Doors: ${formatTimeLabel(row.doors)}   Show: ${formatTimeLabel(row.showTime)}   Ends: ${formatTimeLabel(endTimeOf(row))}${endsNextDay(row.showTime, endTimeOf(row)) ? " +1" : ""}`}
            </span>
            <span className="series-review-list-meta">
              {ticketTypeCountFor(row)} ticket {ticketTypeCountFor(row) === 1 ? "type" : "types"}
            </span>
          </li>
        ))}
      </ul>

      <PublishingCard />

      <StepFooter onBack={() => goToStep("display")}>
        <Button variant="secondary" disabled={saving || !canSave} onClick={save}>
          {saving ? "Saving…" : form.isDraft === false ? "Save Changes" : "Save as Draft"}
        </Button>
        <Button
          variant="primary"
          icon={CheckCircle}
          disabled={saving || !canPublish || missing.length > 0 || form.isDraft === false}
          onClick={() => publish()}
          title={
            form.isDraft === false
              ? "This series has already been published"
              : missing.length > 0
                ? "Fill in the required information first"
                : undefined
          }
        >
          {saving ? "Publishing…" : "Create & Publish"}
        </Button>
      </StepFooter>

      {publishWarnings.length > 0 && (
        <PublishCheckModal
          title={form.name || "this series"}
          warnings={publishWarnings}
          saving={saving}
          onCancel={dismissPublishWarnings}
          onPublish={() => publish(true)}
          onFix={(warning) => {
            dismissPublishWarnings();
            goToStep(warning.step, warning.sub_tab ? { subTab: warning.sub_tab, showType: warning.show_type } : null);
          }}
        />
      )}
    </div>
  );
};

ReviewSection.propTypes = {};

export default ReviewSection;
